import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from './ui/loading-spinner';
import { TermsAcceptanceDialog } from './TermsAcceptanceDialog';
import { rememberPostAuthRedirect } from '@/lib/postAuthRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, needsPasswordChange, needsTermsAcceptance, isOAuthUser, isPaused, userRoles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Carry where they were going (e.g. a contract link from an email) so
      // login lands them there instead of on their default dashboard.
      const target = location.pathname + location.search;
      rememberPostAuthRedirect(target);
      navigate(`/auth?redirect=${encodeURIComponent(target)}`, { replace: true });
      return;
    }

    // Paused accounts: allow only /account-paused
    if (isPaused && location.pathname !== '/account-paused') {
      navigate('/account-paused', { replace: true });
      return;
    }

    // Admin panel (/dashboard) is restricted to admin/dev/instructor.
    // Staff-only users are redirected to their staff dashboard.
    // Exception: /dashboard/avatar-creator is a user-scoped tool open to all authenticated users.
    const isUserScopedDashboardRoute = location.pathname.startsWith('/dashboard/avatar-creator');
    if (location.pathname.startsWith('/dashboard') && !isUserScopedDashboardRoute) {
      const canAccessAdmin =
        userRoles.includes('admin') ||
        userRoles.includes('dev') ||
        userRoles.includes('instructor');
      if (!canAccessAdmin) {
        if (userRoles.includes('staff')) {
          navigate('/staff/institution', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        return;
      }
    }

    // OAuth users never get sent to /setup-password. Terms acceptance is
    // handled by an inline modal (TermsAcceptanceDialog).
    if (isOAuthUser) return;

    // Email/password users: force to /setup-password for pending password or terms.
    if (
      (needsPasswordChange || needsTermsAcceptance) &&
      location.pathname !== '/setup-password'
    ) {
      // Password/terms setup drops the URL; remember the page to return to.
      rememberPostAuthRedirect(location.pathname + location.search);
      navigate('/setup-password', { replace: true });
    }
  }, [user, loading, needsPasswordChange, needsTermsAcceptance, isOAuthUser, isPaused, userRoles, location.pathname, location.search, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      {children}
      {isOAuthUser && needsTermsAcceptance && <TermsAcceptanceDialog />}
    </>
  );
};
